import { Box, Button, Center, Spinner, Text } from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { Icon } from "@src/components/ui-kit/Icon";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useEffect } from "react";
import { DocxPreviewStore } from "./store";

type DocxPreviewProps = {
  readonly url: string;
  readonly onDownload: () => void;
};

export const DocxPreview = observer(function DocxPreview({
  url,
  onDownload,
}: DocxPreviewProps) {
  const store = useLocalObservable(() => new DocxPreviewStore());

  useEffect(() => {
    store.setUrl(url);
    void store.load();

    return () => {
      store.dispose();
    };
  }, [store, url]);

  return (
    <Box pos="relative" overflow="auto" minH="600px" maxH="80vh" bg="grey.50">
      {store.state !== "ready" && (
        <Center
          pos="absolute"
          zIndex="1"
          flexDir="column"
          gap="3"
          bg="white"
          inset="0"
        >
          {store.state === "loading" ? (
            <>
              <Spinner color="blue.600" size="xl" />
              <Text>
                <Trans>Creating preview...</Trans>
              </Text>
            </>
          ) : (
            <>
              <Text>
                <Trans>Preview is not available for this document.</Trans>
              </Text>
              <Button
                leftIcon={<Icon name="download-02" />}
                onClick={onDownload}
                variant="subtle"
              >
                <Trans>Download</Trans>
              </Button>
            </>
          )}
        </Center>
      )}
      <Box
        ref={store.containerRef}
        sx={{
          "& .docx-preview-wrapper": {
            bg: "transparent",
            p: "4",
          },
          "& .docx-preview": {
            bg: "white",
            boxShadow: "sm",
            mx: "auto",
          },
        }}
        display={store.state === "ready" ? "block" : "none"}
      />
    </Box>
  );
});
